import {useCallback, useMemo, useState} from 'react';

import {InboxService} from '../features/company/recruiting/inbox/services/InboxServices';
import {CompanyMatches, InboxFilters} from '../features/company/recruiting/inbox/types/InboxTypes';
import {MatchData, MatchTotalCountPerStatus} from '../types/shared-recruiting-types';
import {PaginationObject} from '../types/shared-types';

export const usePagination = () => {
  const [pagination, setPagination] = useState<PaginationObject>({
    nextPage: null,
    page: 1,
    perPage: 5,
    previousPage: null,
    totalCount: 0,
    totalPages: 0,
  });

  const [totalCountPerStatus, setTotalCountPerStatus] = useState<MatchTotalCountPerStatus>({
    accepted: 0,
    contacted: 0,
    interview: 0,
    hired: 0,
    rejected: 0,
    onHold: 0,
  });

  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [pageLocal, setPageLocal] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const inboxService = useMemo(() => new InboxService(), []);

  const resetData = () => {
    setPageLocal(1);
    setData([]);
  };

  const getMatchesData = useCallback(
    async (url: string, nextPage?: number, filtersParams?: InboxFilters) => {
      let endpointUrl = `${url}&page=${nextPage ?? pageLocal}&per_page=${pagination.perPage}`;

      if (filtersParams?.department && filtersParams?.department?.length > 0) {
        endpointUrl += `&departments=${filtersParams.department.join(',')}`;
      }

      if (filtersParams?.location && filtersParams?.location?.length > 0) {
        endpointUrl += `&locations=${filtersParams.location.join(',')}`;
      }

      if (filtersParams?.tags && filtersParams?.tags?.length > 0) {
        endpointUrl += `&match_tags=${filtersParams.tags.join(',')}`;
      }

      setIsLoading(true);
      setError(false);
      try {
        const response = await inboxService.getPaginatedData(endpointUrl);
        const body = response;

        if (body.data) {
          setTotalCountPerStatus(body.data.totalCountPerStatus as MatchTotalCountPerStatus);
          setData((prevData: CompanyMatches[]) => [...prevData, ...(body?.data?.data as unknown as CompanyMatches[])]);
          setPagination(body.data.pagination as PaginationObject);
          setHasMore(body.data.pagination.nextPage !== null);
        }
      } catch (error) {
        console.error('Failed to fetch inbox items:', error);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [pageLocal, pagination.perPage, inboxService]
  );

  const initializeData = useCallback(
    async (url: string, filtersParams?: InboxFilters) => {
      let initializedEndpointUrl = `${url}&page=${1}&per_page=${pagination.perPage}`;

      if (filtersParams?.department && filtersParams?.department?.length > 0) {
        initializedEndpointUrl += `&departments=${filtersParams.department.join(',')}`;
      }

      if (filtersParams?.location && filtersParams?.location?.length > 0) {
        initializedEndpointUrl += `&locations=${filtersParams.location.join(',')}`;
      }

      if (filtersParams?.tags && filtersParams?.tags?.length > 0) {
        initializedEndpointUrl += `&match_tags=${filtersParams.tags.join(',')}`;
      }

      resetData();
      setIsLoading(true);
      setError(false);
      try {
        const response = await inboxService.getPaginatedData(initializedEndpointUrl);
        const body = response;
        if (body.data) {
          setTotalCountPerStatus(body.data.totalCountPerStatus as MatchTotalCountPerStatus);
          setData(body.data.data as unknown as CompanyMatches[]);
          setPagination(body.data.pagination as PaginationObject);
          setHasMore(body.data.pagination.nextPage !== null);
        }
      } catch (error) {
        console.error('Failed to fetch inbox items:', error);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [pagination.perPage, inboxService]
  );

  const fetchNextPage = useCallback(
    (url: string, filtersParams?: InboxFilters) => {
      if (isLoading || !pagination || pagination.nextPage === null) return;
      setPageLocal((prev: PaginationObject['page']) => prev + 1);
      getMatchesData(url, pageLocal + 1, filtersParams);
    },
    [pagination, pageLocal, isLoading, getMatchesData]
  );

  const updateMatchTags = useCallback(
    (newMatch: MatchData) => {
      const updatedArray = data.map((item) => {
        if (item.resourceId === newMatch.resourceId) {
          return {...item, tags: newMatch.tags};
        }
        return item;
      });
      setData(updatedArray);
    },
    [data]
  );

  return {
    data,
    isLoading,
    pagination,
    error,
    fetchNext: fetchNextPage,
    getMatchesData,
    initializeData,
    hasMore,
    totalCountPerStatus,
    updateMatchTags,
  };
};
